import React, { useContext, useEffect, Fragment, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Nav, Navbar, Row, Col, Collapse } from 'react-bootstrap';
import { navbarBreakPoint } from 'config';
import AppContext from 'context/Context';
import Flex from 'components/common/Flex';
import Logo from 'components/common/Logo';
import NavbarVerticalMenu from './NavbarVerticalMenu';
import ToggleButton from './ToggleButton';
import { capitalize } from 'helpers/utils';
import bgNavbar from 'assets/img/generic/bg-navbar.png';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'context/AuthContext';
import { WORKSPACE_MODES, getWorkspaceMode, isRouteVisibleForMode } from 'helpers/workspace';
import useAgentsList from 'hooks/useAgentsList';
import {
  metricsRoutes,
  marketplaceRoutes,
  operationsRoutes,
  configurationRoutes,
  accessRoutes,
  salesOpsRoutes,
  workspaceRoutes
} from 'routes/siteMaps';

const NavbarVertical = () => {
  const {
    config: {
      navbarStyle,
      isNavbarVerticalCollapsed,
      showBurgerMenu
    }
  } = useContext(AppContext);
  const { t } = useTranslation();
  const { hasPermission, userData, isSuperAdmin } = useAuth();
  const currentMode = getWorkspaceMode(userData);
  const agentsList = useAgentsList();

  const CHANNEL_ICON_MAP = {
    questions: 'mercadolibre',
    messages: 'mercadolibre'
  };

  const agentNavItems = useMemo(
    () =>
      agentsList.map(agent => ({
        id: `agent_${agent.id}`,
        name: agent.name,
        rawLabel: true,
        iconImage: CHANNEL_ICON_MAP[agent.channel] || null,
        icon: CHANNEL_ICON_MAP[agent.channel] ? undefined : 'robot',
        to: `/agents?agent=${agent.id}`,
        active: false,
        modes: ['gestion']
      })),
    [agentsList]
  );

  const injectDynamicAgents = routeGroups =>
    routeGroups.map(group => ({
      ...group,
      children: (group.children || []).map(route => {
        if (!route._dynamicAgents) return route;
        const staticChildren = route.children || [];
        const firstItem = staticChildren.slice(0, 1);
        const rest = staticChildren.slice(1);
        return {
          ...route,
          children: [...firstItem, ...agentNavItems, ...rest]
        };
      })
    }));

  const HTMLClassList = document.getElementsByTagName('html')[0].classList;

  useEffect(() => {
    if (isNavbarVerticalCollapsed) {
      HTMLClassList.add('navbar-vertical-collapsed');
    } else {
      HTMLClassList.remove('navbar-vertical-collapsed');
    }
    return () => {
      HTMLClassList.remove('navbar-vertical-collapsed-hover');
    };
  }, [isNavbarVerticalCollapsed, HTMLClassList]);

  //Control mouseEnter event
  let time = null;
  const handleMouseEnter = () => {
    if (isNavbarVerticalCollapsed) {
      time = setTimeout(() => {
        HTMLClassList.add('navbar-vertical-collapsed-hover');
      }, 100);
    }
  };
  const handleMouseLeave = () => {
    clearTimeout(time);
    HTMLClassList.remove('navbar-vertical-collapsed-hover');
  };

  const filterRoutesByPermission = routeItems =>
    routeItems
      .map(route => {
        if (!isRouteVisibleForMode(currentMode, route.modes)) {
          return null;
        }

        if (route.requiresSuperAdmin && !isSuperAdmin()) {
          return null;
        }

        if (route.children) {
          const filteredChildren = filterRoutesByPermission(route.children);
          if (!filteredChildren.length) {
            return null;
          }
          return { ...route, children: filteredChildren };
        }

        if (route.requiredPermission && !hasPermission(route.requiredPermission)) {
          return null;
        }

        if (
          route.requiredAnyPermissions &&
          !route.requiredAnyPermissions.some(permissionCode =>
            hasPermission(permissionCode)
          )
        ) {
          return null;
        }

        return route;
      })
      .filter(Boolean);

  const getBaseRouteGroups = () => {
    if (currentMode === WORKSPACE_MODES.PARTNERS) {
      return [
        {
          ...workspaceRoutes,
          children: workspaceRoutes.children.filter(route => route.modes?.includes(WORKSPACE_MODES.PARTNERS))
        }
      ];
    }

    if (currentMode === WORKSPACE_MODES.BLOG) {
      return [
        {
          ...workspaceRoutes,
          children: workspaceRoutes.children.filter(route => route.modes?.includes(WORKSPACE_MODES.BLOG))
        }
      ];
    }

    if (currentMode === WORKSPACE_MODES.SALES) {
      return [
        {
          ...workspaceRoutes,
          children: workspaceRoutes.children.filter(route => route.modes?.includes(WORKSPACE_MODES.SALES))
        },
        salesOpsRoutes
      ];
    }

    if (currentMode === WORKSPACE_MODES.SUPERADMIN) {
      return [
        metricsRoutes,
        workspaceRoutes,
        marketplaceRoutes,
        operationsRoutes,
        configurationRoutes,
        accessRoutes
      ];
    }

    return [
      metricsRoutes,
      workspaceRoutes,
      marketplaceRoutes,
      operationsRoutes,
      configurationRoutes,
      accessRoutes
    ];
  };

  const visibleRoutes = injectDynamicAgents(
    getBaseRouteGroups()
      .map(routeGroup => ({
        ...routeGroup,
        children: filterRoutesByPermission(routeGroup.children || [])
      }))
      .filter(routeGroup => (routeGroup.children || []).length > 0)
  );

  const [collapsedSections, setCollapsedSections] = useState({});

  const toggleSection = (label) => {
    setCollapsedSections(prev => ({ ...prev, [label]: !prev[label] }));
  };

  const NavbarLabel = ({ label, isOpen, onToggle }) => (
    <Nav.Item as="li">
      <Row
        className="mt-3 mb-2 navbar-vertical-label-wrapper"
        onClick={onToggle}
        style={{ cursor: 'pointer' }}
      >
        <Col xs="auto" className="navbar-vertical-label navbar-vertical-label">
          {label}
        </Col>
        <Col className="ps-0 d-flex align-items-center">
          <hr className="mb-0 navbar-vertical-divider" style={{ flex: 1 }}></hr>
          <span className="ms-2 navbar-vertical-label" style={{ fontSize: '0.6rem', transition: 'transform 0.2s', transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)' }}>▼</span>
        </Col>
      </Row>
    </Nav.Item>
  );

  return (
    <Navbar
      data-tour="sidebar"
      expand={navbarBreakPoint}
      className={classNames('navbar-vertical', {
        [`navbar-${navbarStyle}`]: navbarStyle !== 'transparent'
      })}
      variant="light"
    >
      <Flex alignItems="center">
        <ToggleButton />
        <Logo at="navbar-vertical" width={40} />
      </Flex>
      <Navbar.Collapse
        in={showBurgerMenu}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{
          backgroundImage:
            navbarStyle === 'vibrant'
              ? `linear-gradient(-45deg, rgba(0, 160, 255, 0.86), #0048a2),url(${bgNavbar})`
              : 'none'
        }}
      >
        <div className="navbar-vertical-content scrollbar">
          <Nav className="flex-column" as="ul">
            {visibleRoutes.map(route => {
              const isOpen = !collapsedSections[route.label];
              return (
                <Fragment key={route.label}>
                  {!route.labelDisable && (
                    <NavbarLabel
                      label={capitalize(t(route.label))}
                      isOpen={isOpen}
                      onToggle={() => toggleSection(route.label)}
                    />
                  )}
                  <Collapse in={isNavbarVerticalCollapsed || isOpen}>
                    <div>
                      <NavbarVerticalMenu routes={route.children} />
                    </div>
                  </Collapse>
                </Fragment>
              );
            })}
          </Nav>
        </div>
      </Navbar.Collapse>
    </Navbar>
  );
};

NavbarVertical.propTypes = {
  label: PropTypes.string
};

export default NavbarVertical;
